import type { BootstrapStatus, DashboardData, OpenItem, SessionUser } from "./types";

const apiUrl = (import.meta.env.VITE_API_URL ?? "http://localhost:3008").replace(/\/+$/, "");

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiUrl}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(body?.error ?? "Something went wrong", response.status);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  bootstrapStatus: () => request<BootstrapStatus>("/v1/auth/bootstrap-status"),
  bootstrap: (email: string, password: string) =>
    request<{ user: SessionUser }>("/v1/auth/bootstrap", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  login: (email: string, password: string) =>
    request<{ user: SessionUser }>("/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>("/v1/auth/logout", { method: "POST" }),
  session: () => request<{ user: SessionUser }>("/v1/auth/session"),
  dashboard: () => request<DashboardData>("/v1/dashboard"),
  users: () => request<{ users: SessionUser[] }>("/v1/users"),
  addUser: (email: string, password: string) =>
    request<{ user: SessionUser }>("/v1/users", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  setGoal: (goal: number) =>
    request<{ goal: number }>("/v1/goal", {
      method: "PUT",
      body: JSON.stringify({ goal }),
    }),
  items: () => request<{ items: OpenItem[] }>("/v1/items"),
  addItem: (input: { title: string; project: string; assignedUserId: string }) =>
    request<{ item: OpenItem }>("/v1/items", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  setItemCompleted: (id: string, completed: boolean) =>
    request<{ item: OpenItem }>(`/v1/items/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ completed }),
    }),
};
